import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  confirmSignIn,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  getCurrentUser,
} from 'aws-amplify/auth';

import { environment } from '../../environments/environment';

import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
  GetIdCommandInput,
  GetCredentialsForIdentityCommandInput,
} from '@aws-sdk/client-cognito-identity';

import {
  CognitoIdentityProviderClient,
  AdminDisableUserCommandInput,
  AdminEnableUserCommand,
  AdminEnableUserCommandInput,
  AdminDisableUserCommand,
} from '@aws-sdk/client-cognito-identity-provider';

export interface IUser {
  email: string;
  givenName: string;
  lastName: string;
  zoneInfo: string;
  phoneNumber: string;
  userType: string;
  address: string;
  password: string;
  newPassword: string;
  showPassword: boolean;
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminCognitoService {
  private authenticationSubject: BehaviorSubject<any>;

  // SRA003-271: in-flight + post-resolve cache of the Cognito GetUser
  // (fetchUserAttributes) call. Shared across concurrent callers within a
  // session; invalidated on adminSignIn / signOut / updateUser /
  // changePasswordUser.
  private userPromise: Promise<any> | null = null;

  constructor() {
    if (!(environment as any).localMode) {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: environment.cognito.userPoolId,
            userPoolClientId: environment.cognito.userPoolWebClientId,
            identityPoolId: `${environment.aws.region}:${environment.aws.identityPoolId}`,
          },
        },
      });
    }
    this.authenticationSubject = new BehaviorSubject<boolean>(false);
  }

  public async adminSignIn(user: IUser): Promise<void> {
    this.userPromise = null;
    // v6 throws "There is already a signed in user" if any session exists
    // (cookies / localStorage / partially-failed prior signIn). v4 silently
    // replaced — preserve that UX by signing the prior session out first.
    await signOut().catch(() => {});

    const { isSignedIn, nextStep } = await signIn({
      username: user.email,
      password: user.password,
    });

    if (
      nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      // First-login flow. v6 keeps the challenge state internal; the caller
      // collects the new password and calls changePasswordUser() which runs
      // confirmSignIn() to complete the challenge AND sign the user in.
      throw 'NEW_PASSWORD_REQUIRED';
    }

    if (!isSignedIn) {
      throw new Error(`Unhandled sign-in step: ${nextStep.signInStep}`);
    }

    await this.exchangeIdentityPoolCredentials();
    this.authenticationSubject.next(true);
  }

  private async exchangeIdentityPoolCredentials(): Promise<void> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('No idToken after signIn');
    }
    const userPoolId = Amplify.getConfig().Auth?.Cognito?.userPoolId;
    if (!userPoolId) {
      throw new Error('Amplify not configured');
    }

    const client = new CognitoIdentityClient({
      region: environment.aws.region,
    });

    const paramsId = {} as GetIdCommandInput;
    paramsId.IdentityPoolId =
      environment.aws.region + ':' + environment.aws.identityPoolId;
    paramsId.Logins = {
      [`cognito-idp.${environment.aws.region}.amazonaws.com/${userPoolId}`]:
        idToken,
    };
    const dataPoolId = await client.send(new GetIdCommand(paramsId));

    const params = {} as GetCredentialsForIdentityCommandInput;
    params.IdentityId = dataPoolId.IdentityId;
    params.Logins = {
      [`cognito-idp.${environment.aws.region}.amazonaws.com/${userPoolId}`]:
        idToken,
    };
    const data = await client.send(new GetCredentialsForIdentityCommand(params));

    localStorage.setItem(
      environment.keys.accessKey,
      data.Credentials?.AccessKeyId || ''
    );
    localStorage.setItem(
      environment.keys.secretKey,
      data.Credentials?.SecretKey || ''
    );
    localStorage.setItem(
      environment.keys.sessionToken,
      data.Credentials?.SessionToken || ''
    );
  }

  public disableUser(username: string): Promise<any> {
    var client = new CognitoIdentityProviderClient({
      region: environment.aws.region,
      credentials: {
        accessKeyId: localStorage.getItem(environment.keys.accessKey) || '',
        secretAccessKey: localStorage.getItem(environment.keys.secretKey) || '',
        sessionToken: localStorage.getItem(environment.keys.sessionToken) || '',
      },
    });
    const params = {} as AdminDisableUserCommandInput;
    params.UserPoolId = environment.cognito.userPoolId;
    params.Username = username;

    const command = new AdminDisableUserCommand(params);

    return client.send(command);
  }

  public enableUser(username: string): Promise<any> {
    var client = new CognitoIdentityProviderClient({
      region: environment.aws.region,
      credentials: {
        accessKeyId: localStorage.getItem(environment.keys.accessKey) || '',
        secretAccessKey: localStorage.getItem(environment.keys.secretKey) || '',
        sessionToken: localStorage.getItem(environment.keys.sessionToken) || '',
      },
    });
    const params = {} as AdminEnableUserCommandInput;
    params.UserPoolId = environment.cognito.userPoolId;
    params.Username = username;

    const command = new AdminEnableUserCommand(params);

    return client.send(command);
  }

  public async changePasswordUser(user: IUser): Promise<void> {
    this.userPromise = null;
    // Completes the NEW_PASSWORD_REQUIRED challenge from adminSignIn().
    // Replaces the v4 ChangePasswordCommand hack — the proper Cognito
    // surface for completing this challenge is confirmSignIn with the new
    // password as the challengeResponse.
    const { isSignedIn } = await confirmSignIn({
      challengeResponse: user.newPassword,
    });
    if (!isSignedIn) {
      throw new Error('confirmSignIn did not complete the challenge');
    }
    await this.exchangeIdentityPoolCredentials();
    this.authenticationSubject.next(true);
  }

  public signOut(): Promise<any> {
    this.userPromise = null;
    if ((environment as any).localMode) {
      localStorage.removeItem(environment.keys.accessToken);
      this.authenticationSubject.next(false);
      return Promise.resolve();
    }
    return signOut().then(() => {
      this.authenticationSubject.next(false);
    });
  }

  async isAuthenticated(): Promise<boolean> {
    if ((environment as any).localMode) {
      return !!localStorage.getItem(environment.keys.accessToken);
    }
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.idToken;
    } catch {
      return false;
    }
  }

  public async isAdmin(): Promise<boolean> {
    if ((environment as any).localMode) {
      const token = localStorage.getItem(environment.keys.accessToken);
      if (!token) return false;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return (payload['custom:user_type'] || '').toLowerCase() === 'admin';
      } catch {
        return false;
      }
    }
    const userInfo = await this.getUser();
    return userInfo?.attributes?.['custom:user_type'] === 'ADMIN';
  }

  public getUser(): Promise<any> {
    if (!this.userPromise) {
      this.userPromise = this.fetchUser().catch((err) => {
        // Don't cache failures — next call retries.
        this.userPromise = null;
        throw err;
      });
    }
    return this.userPromise;
  }

  private async fetchUser(): Promise<any> {
    if ((environment as any).localMode) {
      const token = localStorage.getItem(environment.keys.accessToken);
      if (!token) return null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          username: payload.sub,
          attributes: {
            email: payload.email,
            given_name: payload.given_name || '',
            family_name: payload.family_name || '',
            'custom:user_type': (payload['custom:user_type'] || '').toUpperCase(),
          },
        };
      } catch {
        return null;
      }
    }
    const attrs = (await fetchUserAttributes()) as Record<string, string>;
    return { attributes: attrs };
  }

  public async updateUser(user: any): Promise<void> {
    if ((environment as any).localMode) {
      return;
    }
    const source: Record<string, string> = user?.attributes ?? user ?? {};
    const attrs = { ...source };
    delete attrs['custom:user_type']; //immutable attribute
    await updateUserAttributes({ userAttributes: attrs });
    this.userPromise = null;
  }

  async isLoggedIn(): Promise<boolean> {
    if ((environment as any).localMode) {
      return !!localStorage.getItem(environment.keys.accessToken);
    }
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }
}
